import { ReportSkillId, ReportSkillTrend } from "@/generated/graphql";
import type { Dictionary, TranslationKey } from "@/i18n";
import { format } from "@/i18n";
import type { IconType } from "react-icons";
import { HiArrowDown, HiArrowRight, HiArrowUp } from "react-icons/hi";

/**
 * The report's own palette and copy, mirroring the student app's report
 * (apps/b2b-mobile/src/features/report/constants) so a teacher reads the same colors and wording
 * their student does. These are product tokens for this one screen, not app-wide theme colors.
 */

const SKILL_LABEL_KEYS: Record<ReportSkillId, TranslationKey> = {
  [ReportSkillId.Speaking]: "report.skill.speaking",
  [ReportSkillId.Listening]: "report.skill.listening",
  [ReportSkillId.Reading]: "report.skill.reading",
  [ReportSkillId.Grammar]: "report.skill.grammar",
  [ReportSkillId.Writing]: "report.skill.writing",
  [ReportSkillId.WordRecognition]: "report.skill.wordRecognition",
};

export const REPORT_SKILL_IDS = Object.keys(SKILL_LABEL_KEYS) as ReportSkillId[];

export function skillLabel(t: Dictionary, skill: ReportSkillId): string {
  return t[SKILL_LABEL_KEYS[skill]];
}

interface TrendStyle {
  icon: IconType;
  color: string;
  background: string;
}

/** Green/amber/red, used for both skill trends and the practice-time comparison chip. */
export const TREND_STYLES: Record<ReportSkillTrend, TrendStyle> = {
  [ReportSkillTrend.Improving]: { icon: HiArrowUp, color: "#2E9E4F", background: "#E3F6E9" },
  [ReportSkillTrend.Steady]: { icon: HiArrowRight, color: "#F5A623", background: "#FDF0DC" },
  [ReportSkillTrend.Declining]: { icon: HiArrowDown, color: "#E53935", background: "#FCE4E4" },
};

/** A skill with no scored practice at all — deliberately not the same grey as an empty track,
 * so "not unlocked yet" doesn't read as "measured at zero". */
export const UNAVAILABLE_SKILL = { color: "#ABABAB", background: "#EDEDED" } as const;

/** A real score built on too little practice to trust yet. Amber rather than the unavailable grey
 * — the number IS shown, so this reads as "take it with a pinch of salt", not "not measured". */
export const PROVISIONAL_SKILL = { color: "#F5A623", background: "#FDF0DC" } as const;

interface PerformanceBand {
  min: number;
  /** The flame's three nested layers, each an outer-to-inner gradient pair. */
  outer: readonly [string, string];
  mid: readonly [string, string];
  inner: readonly [string, string];
  glow: string;
  glowStrength: number;
  energy: number;
}

/** Six bands warming from a cold blue flame at the bottom to near-white-hot at 100, mirroring
 * AnimatedFlame.tsx in the student app. Color, glow and breathing speed all move together. */
const PERFORMANCE_BANDS: readonly PerformanceBand[] = [
  {
    min: 0,
    outer: ["#1E3FA8", "#3A6FE0"],
    mid: ["#2A55C8", "#4E8CF0"],
    inner: ["#4FA8FF", "#EAF7FF"],
    glow: "#3A6FE0",
    glowStrength: 0.08,
    energy: 0.68,
  },
  {
    min: 25,
    outer: ["#FFA94A", "#FFD27A"],
    mid: ["#FFBD5C", "#FFDE96"],
    inner: ["#FFD27A", "#FFEFC0"],
    glow: "#FFB84A",
    glowStrength: 0.22,
    energy: 0.85,
  },
  {
    min: 50,
    outer: ["#FF8A24", "#FFB84A"],
    mid: ["#FF9A3D", "#FFC26A"],
    inner: ["#FFC24A", "#FFE79A"],
    glow: "#FF8A24",
    glowStrength: 0.4,
    energy: 1.0,
  },
  {
    min: 70,
    outer: ["#FF6A1A", "#FF8F2A"],
    mid: ["#FF7A20", "#FFA23D"],
    inner: ["#FFA83D", "#FFCB6A"],
    glow: "#FF6A1A",
    glowStrength: 0.58,
    energy: 1.2,
  },
  {
    min: 85,
    outer: ["#FF4A14", "#FF6A1A"],
    mid: ["#FF641A", "#FF8A24"],
    inner: ["#FF8A24", "#FFE066"],
    glow: "#FF4A14",
    glowStrength: 0.78,
    energy: 1.4,
  },
  {
    min: 100,
    outer: ["#E8300A", "#FF3D14"],
    mid: ["#FF4A14", "#FF7A1F"],
    inner: ["#FF5C14", "#FFF6D9"],
    glow: "#E8300A",
    glowStrength: 0.98,
    energy: 1.65,
  },
] as const;

export function getPerformanceVisual(percent: number): PerformanceBand {
  const clamped = Math.max(0, Math.min(100, percent));
  const band = [...PERFORMANCE_BANDS].reverse().find((item) => clamped >= item.min);
  return band ?? (PERFORMANCE_BANDS[0] as PerformanceBand);
}

/** `#RRGGBB` -> `rgba(r, g, b, alpha)`, so a color can fade out to fully transparent. */
export function hexToRgba(hex: string, alpha: number): string {
  const clean = hex.replace("#", "");
  const r = Number.parseInt(clean.slice(0, 2), 16);
  const g = Number.parseInt(clean.slice(2, 4), 16);
  const b = Number.parseInt(clean.slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

const PERFORMANCE_MESSAGES = [
  { min: 0, message: "report.performance.band0", sub: "report.performance.subLow" },
  { min: 40, message: "report.performance.band1", sub: "report.performance.subMid" },
  { min: 60, message: "report.performance.band2", sub: "report.performance.subMid" },
  { min: 80, message: "report.performance.band3", sub: "report.performance.subMid" },
  { min: 95, message: "report.performance.band4", sub: "report.performance.subHigh" },
] as const satisfies readonly { min: number; message: TranslationKey; sub: TranslationKey }[];

/** Wording changes on its own five bands, not the six color ones. */
function getPerformanceBand(percent: number): (typeof PERFORMANCE_MESSAGES)[number] {
  const clamped = Math.max(0, Math.min(100, percent));
  const band = [...PERFORMANCE_MESSAGES].reverse().find((item) => clamped >= item.min);
  return band ?? PERFORMANCE_MESSAGES[0];
}

export function getPerformanceMessage(t: Dictionary, percent: number): string {
  return t[getPerformanceBand(percent).message];
}

export function getPerformanceSubMessage(t: Dictionary, percent: number): string {
  return t[getPerformanceBand(percent).sub];
}

/** "45 min" / "2h" / "2h 14m" — the report's one duration format. */
export function formatPracticeTime(t: Dictionary, ms: number): string {
  const totalMinutes = Math.round(ms / 60000);
  if (totalMinutes < 60) return format(t["report.duration.minutes"], { count: totalMinutes });
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return minutes > 0
    ? format(t["report.duration.hoursMinutes"], { hours, minutes })
    : format(t["report.duration.hours"], { hours });
}
